import { CreateReturnProductAppBySourceOrderPayload } from "@api/sharing/ReturnProductManagementApi";
import { ReturnSubOrderReason } from "@constant/ReturnOrderReason";
import { returnTypeOptions } from "@constant/ReturnOrderStatus";
import { createReturnProductAppBySourceOrder, SourceOrderPrefill } from "@redux/sharing/ReturnProductManagementSlice";
import cityRegionData from "@utils/CityRegionData";
import formatEffectiveDateBatch from "@utils/formatEffectiveDateBatch";
import { useAppDispatch } from "src/store";

import React, { useState } from "react";
import { Button, Col, Descriptions, Row, Space, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import styled from "styled-components";

import { Step1FormValues } from "./ReturnProductAppCreateStep1";
import { Step2ProductRow } from "./ReturnProductAppCreateStep2";

const Wrapper = styled.div`
  max-width: 960px;
`;
const SectionRow = styled(Row)`
  margin: 24px 0 8px;
`;
const FooterRow = styled(Row)`
  margin-top: 16px;
`;

type Props = {
  prefill: SourceOrderPrefill;
  step1Values: Step1FormValues;
  productRows: Step2ProductRow[];
  onBackStep: () => void;
};

export default function ReturnProductAppCreateStep3(props: Props) {
  const { prefill, step1Values, productRows, onBackStep } = props;
  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedRows = productRows.filter((row) => row.checked);
  const returnTypeLabel = returnTypeOptions.find((option) => option.value === step1Values.isReturnToWarehouse)?.label;
  const receiverAddress = `${step1Values.city || ""}${step1Values.region || ""}${step1Values.addressDetail || ""}`;
  const receiverZipcode =
    step1Values.city && step1Values.region
      ? Number(cityRegionData[step1Values.city][step1Values.region])
      : prefill.receiverZipcode || 0;

  const returnReason =
    step1Values.returnReason === ReturnSubOrderReason.OTHER
      ? step1Values.returnReasonOther || ""
      : step1Values.returnReason;

  const handleSubmit = () => {
    const payload: CreateReturnProductAppBySourceOrderPayload = {
      originalPlatformNumber: prefill.originalPlatformNumber,
      receiverName: step1Values.receiverName,
      receiverPhone: step1Values.receiverPhone,
      receiverAddress,
      receiverZipcode,
      note: step1Values.note || "",
      isReturnToWarehouse: step1Values.isReturnToWarehouse,
      returnReason,
      productInfo: selectedRows.map((row) => ({ id: row.productId, qty: row.returnQty })),
    };
    setIsSubmitting(true);
    dispatch(createReturnProductAppBySourceOrder(payload))
      .unwrap()
      .catch(() => {})
      .finally(() => setIsSubmitting(false));
    // 成功後停留在 Step3，成功彈窗由 thunk 內的 Modal.warning 處理，不 navigate
    // 錯誤訊息已由 CoreAPI interceptor 的 handleCustomError 顯示，這裡只接住 rejection 避免 unhandled promise rejection
  };

  const columns: ColumnsType<Step2ProductRow> = [
    {
      title: "SKU / 商品名稱",
      dataIndex: "sku",
      render: (sku: string | null, record) => (
        <span>
          {sku ?? "-"} / {record.productName}
        </span>
      ),
    },
    {
      title: "效期_批號",
      dataIndex: "batchNumber",
      render: (batchNumber: string | null, record) => formatEffectiveDateBatch(record.effectiveDate, batchNumber),
    },
    { title: "退貨數量", dataIndex: "returnQty" },
  ];

  return (
    <Wrapper>
      <Descriptions title="退貨單基本資料" column={1} bordered>
        <Descriptions.Item label="退貨通路/平台">{prefill.platform || "-"}</Descriptions.Item>
        <Descriptions.Item label="原始訂單單號">{prefill.originalPlatformNumber || "-"}</Descriptions.Item>
        <Descriptions.Item label="退件人名稱">{step1Values.receiverName}</Descriptions.Item>
        <Descriptions.Item label="退件人電話">{step1Values.receiverPhone || "-"}</Descriptions.Item>
        <Descriptions.Item label="退件人地址">{receiverAddress || "-"}</Descriptions.Item>
        <Descriptions.Item label="退貨類型">{returnTypeLabel}</Descriptions.Item>
        <Descriptions.Item label="退貨原因">{returnReason}</Descriptions.Item>
        <Descriptions.Item label="備註">{step1Values.note || "-"}</Descriptions.Item>
      </Descriptions>
      <SectionRow>
        <Col>退貨商品（{selectedRows.length}）</Col>
      </SectionRow>
      <Table rowKey="productId" columns={columns} dataSource={selectedRows} pagination={false} />
      <FooterRow justify="end">
        <Space>
          <Button onClick={onBackStep}>返回</Button>
          <Button type="primary" loading={isSubmitting} onClick={handleSubmit}>
            確認送出
          </Button>
        </Space>
      </FooterRow>
    </Wrapper>
  );
}
